/**
 * BuddyGroupDetailPage — read-only detail view for a single buddy group.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useBuddyGroup } from '../api';
import { useBuddyGroupPermissions } from '../auth/use-buddy-group-permissions';
import { BuddyGroupMetadata } from '../components/BuddyGroupMetadata';
import { BuddyGroupMemberList } from '../components/BuddyGroupMemberList';
import { DissolveGroupButton } from '../components/DissolveGroupButton';

export default function BuddyGroupDetailPage() {
  const { rodeoId, groupId } = useParams<{ rodeoId: string; groupId: string }>();
  const navigate = useNavigate();
  const { canEdit, canDissolve } = useBuddyGroupPermissions();
  const { data, isLoading, isError } = useBuddyGroup(groupId!);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to List
        </Button>
        <div className="text-sm text-destructive">Error loading buddy group.</div>
      </div>
    );
  }

  const { group } = data;

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups`)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold">{group.group_name || '(unnamed group)'}</h1>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups/${groupId}/edit`)}>
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
          {canDissolve && (
            <DissolveGroupButton
              group={group}
              onDissolved={() => navigate(`/rodeos/${rodeoId}/buddy-groups`)}
            />
          )}
        </div>
      </div>

      {/* Metadata card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Group Details</CardTitle>
        </CardHeader>
        <CardContent>
          <BuddyGroupMetadata group={group} />
        </CardContent>
      </Card>

      {/* Members card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Members ({group.members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <BuddyGroupMemberList members={group.members} />
        </CardContent>
      </Card>
    </div>
  );
}
